import { useEffect, useRef, useState } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';

import {
  Avatar,
  Button,
  IconButton,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
  type PopperProps,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import type { CommentResponse } from '@/designs-dev/backend/client';
import {
  addComment,
  contentOf,
  isResolved,
  markSeen,
  removeComment,
  repliesTo,
  selectThread,
  setResolved,
  useComments,
} from '@/designs-dev/comments/store';

// One thread: its comments and a reply box. Shown in a bubble beside its pin
// when the pin is clicked, the way Figma opens a comment; the panel is only the
// list of them.

const ago = (iso: string) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  const rtf = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });
  if (s < 60) return 'just now';
  if (s < 3600) return rtf.format(-Math.round(s / 60), 'minute');
  if (s < 86400) return rtf.format(-Math.round(s / 3600), 'hour');
  return rtf.format(-Math.round(s / 86400), 'day');
};

const nameOf = (c: CommentResponse) =>
  c.author.fullName ?? c.author.givenName ?? c.author.email ?? 'Someone';

export const Message = ({
  comment,
  mine,
  label,
}: {
  comment: CommentResponse;
  mine: boolean;
  label: string;
}) => (
  <Stack spacing={0.5}>
    <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
      <Avatar
        src={comment.author.picture ?? undefined}
        sx={{ width: 24, height: 24, typography: 'caption' }}
      >
        {nameOf(comment)[0]}
      </Avatar>
      <Typography variant='body2' sx={{ flex: 1, minWidth: 0 }} noWrap>
        {nameOf(comment)}
      </Typography>
      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
        {ago(comment.createdAt)}
      </Typography>
      {mine && (
        <IconButton
          size='small'
          aria-label={label}
          onClick={(e) => {
            e.stopPropagation();
            removeComment(comment.id).catch((error: unknown) =>
              console.error('deleting comment failed', error),
            );
          }}
        >
          <Icon icon={'trash' as never} fontSize={12} />
        </IconButton>
      )}
    </Stack>
    <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', pl: 4 }}>
      {contentOf(comment).text}
    </Typography>
  </Stack>
);

const Reply = ({ thread }: { thread: CommentResponse }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const send = () => {
    if (!text.trim() || sending) return;
    setSending(true);
    addComment(thread.anchor, { text: text.trim() }, thread.id)
      .then(() => setText(''))
      .catch((error: unknown) => console.error('posting reply failed', error))
      .finally(() => setSending(false));
  };
  return (
    <TextField
      size='small'
      multiline
      autoFocus
      placeholder='Reply'
      value={text}
      disabled={sending}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          send();
        }
      }}
    />
  );
};

// The selected thread, beside its pin. Pins carry data-comment-pin so the
// bubble can find whichever one is open without the pin handing over a ref.
export const ThreadBubble = () => {
  const { user } = useKindeAuth();
  const { comments, selected } = useComments();
  const popper = useRef<{ update: () => unknown } | null>(null);

  // A pin on a canvas moves with a transform, which nothing tells the popper
  // about, so it follows the pin frame by frame while it is open.
  useEffect(() => {
    if (!selected) return;
    let frame = requestAnimationFrame(function follow() {
      void popper.current?.update();
      frame = requestAnimationFrame(follow);
    });
    return () => cancelAnimationFrame(frame);
  }, [selected]);

  // Opening a thread reads it, and so does anything said while it's open.
  useEffect(() => {
    if (selected) markSeen(selected);
  }, [selected, comments]);

  const thread = comments.find((c) => c.id === selected && !c.parentId);
  if (!thread) return null;
  const replies = repliesTo(comments, thread.id);
  const resolved = isResolved(comments, thread.id);

  return (
    <Popper
      open
      popperRef={popper as PopperProps['popperRef']}
      placement='right-start'
      anchorEl={() =>
        document.querySelector(`[data-comment-pin="${thread.id}"]`) ??
        document.body
      }
      sx={{ zIndex: 1400 }}
      modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
    >
      <Paper
        // Clicks in the bubble are for the bubble, not the screen under it.
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: 300,
          maxHeight: 420,
          overflowY: 'auto',
          p: 1.5,
          border: 1,
          borderColor: 'custom.neutral.200',
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: -1,
            }}
          >
            {/* Resolving puts the thread away, as Figma does, so the bubble
                closes with it. Reopening keeps it open to carry on. */}
            <Button
              size='small'
              startIcon={<Icon icon={'check' as never} fontSize={12} />}
              onClick={() =>
                setResolved(thread, !resolved)
                  .then(() => {
                    if (!resolved) selectThread(null);
                  })
                  .catch((error: unknown) =>
                    console.error('resolving thread failed', error),
                  )
              }
            >
              {resolved ? 'Reopen' : 'Resolve'}
            </Button>
            <IconButton
              size='small'
              aria-label='Close'
              onClick={() => selectThread(null)}
            >
              <Icon icon={'xmark' as never} fontSize={12} />
            </IconButton>
          </Stack>
          <Message
            comment={thread}
            mine={thread.author.id === user?.id}
            label='Delete thread'
          />
          {replies.map((r) => (
            <Message
              key={r.id}
              comment={r}
              mine={r.author.id === user?.id}
              label='Delete reply'
            />
          ))}
          <Reply key={thread.id} thread={thread} />
        </Stack>
      </Paper>
    </Popper>
  );
};
